// Itinerary time helpers — shared by the Schedule page, its editor, and the
// dashboards that render a slice of the agenda.
//
// Storage shape: dates are ISO 'YYYY-MM-DD', times are 24h 'HH:MM' strings.
// A block that runs past midnight (the Friday DJ night, the Saturday dinner)
// cannot be described by endTime alone — '00:00' sorts before '19:30' — so an
// item carries one of:
//   • endsNextDay: true  → endTime is a real time on the FOLLOWING day
//   • openEnded:   true  → no published finish at all ("till late")
// Everything here tolerates missing or malformed values, because rows are
// hand-typed by admins and older rows predate these flags.

export const MINUTES_PER_DAY = 1440;

// 'HH:MM' → minutes since midnight, or null when unparseable.
export function toMinutes(hhmm) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function startMinutes(item) {
  return toMinutes(item && item.startTime);
}

// End of the block on the same timeline as startMinutes — so a 19:30 → 00:30
// block ends at 1470, not 30, and durations and sorting stay correct.
// Returns null when the block has no published end.
export function endMinutes(item) {
  if (!item || item.openEnded) return null;
  const end = toMinutes(item.endTime);
  if (end === null) return null;
  const start = startMinutes(item);
  // Trust an explicit flag; otherwise infer the wrap, so rows saved before
  // endsNextDay existed still sort and measure correctly.
  if (item.endsNextDay || (start !== null && end <= start)) return end + MINUTES_PER_DAY;
  return end;
}

// 'HH:MM' → '1:30 PM'. Also accepts a raw minute count past 1440.
export function formatTime(hhmm) {
  const total = typeof hhmm === 'number' ? hhmm : toMinutes(hhmm);
  if (total === null) return '--:--';
  const hours24 = Math.floor(total / 60) % 24;
  const minutes = total % 60;
  const suffix = hours24 < 12 ? 'AM' : 'PM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

// '1:30 PM – 4:00 PM', '7:30 PM – Late', or just the start when no end is set.
export function timeRangeLabel(item) {
  if (!item) return '';
  const start = item.startTime ? formatTime(item.startTime) : null;
  if (item.openEnded) return start ? `${start} – Late` : 'Till late';
  if (!item.endTime) return start || 'Time TBD';
  const end = `${formatTime(item.endTime)}${item.endsNextDay ? ' (+1)' : ''}`;
  return start ? `${start} – ${end}` : end;
}

// '2h 30m' / '45m' / 'Till late' / '' when it cannot be worked out.
export function durationLabel(item) {
  if (!item) return '';
  if (item.openEnded) return 'Till late';
  const start = startMinutes(item);
  const end = endMinutes(item);
  if (start === null || end === null) return '';
  const total = end - start;
  if (total <= 0) return '';
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return [hours ? `${hours}h` : null, minutes ? `${minutes}m` : null].filter(Boolean).join(' ');
}

// A Date → local ISO date. Never use toISOString() here: it shifts to UTC and
// silently reports "yesterday" for anyone east of Greenwich after 05:30 IST.
export function toISODate(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

// '2026-09-04' → 'Friday, 4 September'
export function longDateLabel(iso) {
  if (!iso) return 'Unscheduled';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
}

// '2026-09-04' → 'Fri 4 Sep' (compact, for the day tabs)
export function shortDateLabel(iso) {
  if (!iso) return 'Unscheduled';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}

// Chronological: by date, then start time, then title so two blocks that start
// together keep a stable order.
export function compareItems(a, b) {
  const byDate = String(a.date || '').localeCompare(String(b.date || ''));
  if (byDate) return byDate;
  const startA = startMinutes(a);
  const startB = startMinutes(b);
  if (startA !== startB) {
    if (startA === null) return 1;
    if (startB === null) return -1;
    return startA - startB;
  }
  return String(a.title || '').localeCompare(String(b.title || ''));
}

export function sortItems(list) {
  return [...(list || [])].sort(compareItems);
}

// 'past' | 'live' | 'upcoming' against the wall clock.
export function statusOf(item, now = new Date()) {
  if (!item || !item.date) return 'upcoming';
  const today = toISODate(now);
  if (item.date < today) return 'past';
  if (item.date > today) return 'upcoming';

  const current = now.getHours() * 60 + now.getMinutes();
  const start = startMinutes(item);
  if (start === null) return 'upcoming';
  if (current < start) return 'upcoming';
  const end = endMinutes(item);
  // An open-ended block stays live for the rest of its own day.
  if (end === null) return 'live';
  return current < end ? 'live' : 'past';
}

// The single item to badge "UP NEXT" — the earliest one still ahead of us.
export function nextUpId(items, now = new Date()) {
  const upcoming = sortItems(items).find((item) => statusOf(item, now) === 'upcoming');
  return upcoming ? upcoming.id : null;
}

// A Google Maps link for the block venue. An explicit mapsUrl wins; otherwise
// search on "<venue>, <address>", which resolves fine even when only one of the
// two is filled in. Returns null when there is nothing to point at.
export function mapsHref(item) {
  if (!item) return null;
  if (item.mapsUrl) return item.mapsUrl;
  const query = [item.location, item.address].filter(Boolean).join(', ');
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

// Sub-slots, in start order, with the blank rows dropped.
export function agendaOf(item) {
  return (item && Array.isArray(item.agenda) ? item.agenda : [])
    .filter((slot) => slot && (slot.title || slot.startTime))
    .sort((a, b) => {
      const startA = toMinutes(a.startTime);
      const startB = toMinutes(b.startTime);
      if (startA === startB) return 0;
      if (startA === null) return 1;
      if (startB === null) return -1;
      return startA - startB;
    });
}

// Group sorted items by calendar date, preserving chronological day order.
export function groupByDay(items) {
  const groups = new Map();
  for (const item of sortItems(items)) {
    const key = item.date || '';
    if (!groups.has(key)) {
      groups.set(key, { date: key, day: item.day || '', theme: item.dayTheme || '', items: [] });
    }
    const group = groups.get(key);
    // First non-empty label/theme on the day wins — they are per-day facts
    // duplicated onto every row.
    if (!group.day && item.day) group.day = item.day;
    if (!group.theme && item.dayTheme) group.theme = item.dayTheme;
    group.items.push(item);
  }
  return Array.from(groups.values());
}
